/**
 * Pricing config — single source of truth lives on the backend at
 * GET /api/pricing-config. This module fetches it once at app load and caches
 * an in-memory snapshot. The exported helpers stay synchronous so call-sites
 * (OrderContext helpers, step components) don't need any async rework.
 *
 * Until the fetch completes, the FALLBACK_* values below are used for
 * first-paint. They should match the backend defaults so the UX is identical
 * whether the fetch has resolved or not.
 */
import api from "./api";

// ---- Fallback snapshot (first-paint, offline, or fetch failure) ----
const FALLBACK_WINDOW_BULK_TIERS = [
  { minQty: 5, dollarOff: 25 },
  { minQty: 8, dollarOff: 60 },
  { minQty: 12, dollarOff: 120 },
  { minQty: 20, dollarOff: 200 },
];

const FALLBACK_WHITE_GLOVE_TIERS = [
  { minQty: 3, fee: 49 },
  { minQty: 6, fee: 79 },
  { minQty: 11, fee: 99 },
];

const FALLBACK_MESH_OPTIONS = [
  { id: "standard", name: "Standard Mesh (Included)", upcharge: 0,
    blurb: "Durable, high-quality fiberglass mesh for everyday use." },
  { id: "ultraview", name: "UltraView Mesh Upgrade", upcharge: 10,
    blurb: "Improved visibility with a clearer, more open view." },
  { id: "no_see_ums", name: "No-See-Ums Mesh Upgrade", upcharge: 10,
    blurb: "Tightly woven mesh to block even the smallest bugs and insects." },
  { id: "solar_80", name: "80% Solar Screen Upgrade", upcharge: 15,
    blurb: "Blocks heat and UV rays to help keep your home cooler and reduce energy costs." },
];

const FALLBACK_FEES = {
  petscreen_per_item: 25,
  pro_measurement: 39,
  delivery: 39,
  delivery_install: 99,
};

const FALLBACK_TAX = { ca_rate: 0.0875 };

// ---- Mutable snapshot (updated by loadPricingConfig) ----
const snapshot = {
  windowBulkTiers: [...FALLBACK_WINDOW_BULK_TIERS],
  whiteGloveTiers: [...FALLBACK_WHITE_GLOVE_TIERS],
  meshOptions: [...FALLBACK_MESH_OPTIONS],
  fees: { ...FALLBACK_FEES },
  tax: { ...FALLBACK_TAX },
  loaded: false,
};

let inflight = null;

/** Fetch the authoritative config from the backend. Idempotent — resolves
 *  to the current snapshot whether or not the fetch succeeds. */
export async function loadPricingConfig() {
  if (inflight) return inflight;
  inflight = (async () => {
    try {
      const r = await api.get("/pricing-config");
      const d = r.data || {};
      // Mutate arrays in-place so previously-imported exports stay valid.
      if (Array.isArray(d.window_bulk_tiers) && d.window_bulk_tiers.length) {
        snapshot.windowBulkTiers.splice(0, snapshot.windowBulkTiers.length, ...d.window_bulk_tiers);
      }
      if (Array.isArray(d.white_glove_tiers) && d.white_glove_tiers.length) {
        snapshot.whiteGloveTiers.splice(0, snapshot.whiteGloveTiers.length, ...d.white_glove_tiers);
      }
      if (Array.isArray(d.mesh_options) && d.mesh_options.length) {
        snapshot.meshOptions.splice(0, snapshot.meshOptions.length, ...d.mesh_options);
      }
      if (d.fees) Object.assign(snapshot.fees, d.fees);
      if (d.tax) Object.assign(snapshot.tax, d.tax);
      snapshot.loaded = true;
    } catch (_e) {
      // Silent fail: fallback values remain in place.
    }
    return snapshot;
  })();
  return inflight;
}

// ---- Sync getters used throughout the UI ----
export const WINDOW_BULK_TIERS = snapshot.windowBulkTiers; // eslint-disable-line
export const WHITE_GLOVE_TIERS = snapshot.whiteGloveTiers; // eslint-disable-line
export const MESH_OPTIONS = snapshot.meshOptions; // eslint-disable-line

export function getMeshOptions() { return snapshot.meshOptions; }
export function getWindowBulkTiers() { return snapshot.windowBulkTiers; }
export function getWhiteGloveTiers() { return snapshot.whiteGloveTiers; }
export function getFees() { return snapshot.fees; }
export function getCaTaxRate() { return snapshot.tax.ca_rate; }

export function bulkDiscountFor(productType, qty) {
  if (productType !== "window") return 0;
  let best = 0;
  for (const t of snapshot.windowBulkTiers) if (qty >= t.minQty) best = t.dollarOff;
  return best;
}

export function nextBulkTier(qty) {
  for (const t of snapshot.windowBulkTiers) {
    if (qty < t.minQty) return { screensAway: t.minQty - qty, dollarOff: t.dollarOff };
  }
  return null;
}

export function whiteGloveFee(productType, qty, selected) {
  if (!selected || productType !== "window" || qty < 3) return 0;
  // Iterate high→low so the biggest matching tier wins.
  const tiers = [...snapshot.whiteGloveTiers].sort((a, b) => b.minQty - a.minQty);
  for (const t of tiers) if (qty >= t.minQty) return t.fee;
  return 0;
}

export function meshById(id) {
  return snapshot.meshOptions.find((m) => m.id === id) || snapshot.meshOptions[0];
}
